import React from "react";
import "../home/home.css";
import { Form, Input, Button, Select, message } from "antd";
import { useTranslation } from "react-i18next";
import { useScrollReveal } from "../utils/useScrollReveal";

const { Option } = Select;

export default function Contact({ isLoggedIn, setLoginOpen, setAccountOpen, setPricingOpen, setFaqOpen }) {
  const [form] = Form.useForm();
  const { t, i18n } = useTranslation();

  // Scroll reveal hooks
  const titleSection = useScrollReveal({ threshold: 0.2 });
  const formSection = useScrollReveal({ threshold: 0.1 });

  const changeLanguage = () => {
    if (i18n.language === "en") i18n.changeLanguage("fr");
    else i18n.changeLanguage("en");
  };

  const onFinish = async (values) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/contact/send-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values)
      });

      const result = await response.json();
      if (result.status === "success") {
        message.success("✅ Your message has been sent successfully!");
        form.resetFields();
      } else {
        message.error("❌ Sending failed: " + result.detail);
      }
    } catch (error) {
      console.error("Client error:", error);
      message.error("❌ Server connection failed.");
    }
  };

  const clickLogin = () => setLoginOpen(true);

  return (
    <div className="contact-page w-full flex flex-col items-center bg-[#f5f5f7] min-h-[100vh]">

      {/* Main Container */}
      <div className="w-full max-w-[1100px] px-6 py-12 md:py-16 flex-grow flex flex-col items-center justify-center">
        
        {/* Title OUTSIDE the block, matched to About page style */}
        <h1 
          ref={titleSection.ref}
          className={`text-4xl md:text-5xl font-extrabold text-center text-gray-900 mb-8 scroll-reveal ${titleSection.isVisible ? 'visible' : ''}`}
        >
            {t("contact.title")}
        </h1>

        {/* White Block */}
        <div 
          ref={formSection.ref}
          className={`w-full bg-white shadow-xl rounded-3xl p-8 md:p-10 scroll-reveal-scale ${formSection.isVisible ? 'visible' : ''}`}
        >
          <Form
            form={form}
            onFinish={onFinish}
            layout="vertical"
            size="middle"
            // Removed 'space-y-6' to keep elements closer, relied on standard AntD spacing + grid gaps
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item name="firstName" label={t("contact.firstName")} rules={[{ required: true }]}>
                <Input placeholder={t("contact.firstName")} className="rounded-md" />
              </Form.Item>
              <Form.Item name="lastName" label={t("contact.lastName")} rules={[{ required: true }]}>
                <Input placeholder={t("contact.lastName")} className="rounded-md" />
              </Form.Item>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Form.Item name="email" label={t("contact.email")} rules={[{ required: true, type: "email" }]}>
                <Input placeholder={t("contact.email")} className="rounded-md" />
              </Form.Item>
              <Form.Item name="industry" label={t("contact.industry")} rules={[{ required: true }]}>
                <Select placeholder={t("contact.selectIndustry")} className="rounded-md">
                  <Option value="Civil Engineering">{t("contact.civilEngineering")}</Option>
                  <Option value="Transportation Engineering">{t("contact.transportationEngineering")}</Option>
                  <Option value="Structural Engineering">{t("contact.structuralEngineering")}</Option>
                </Select>
              </Form.Item>
              <Form.Item name="organization" label={t("contact.organization")}>
                <Input placeholder={t("contact.organization")} className="rounded-md" />
              </Form.Item>
            </div>

            <Form.Item name="referral" label={t("contact.referral")} className="mt-2">
              <Input placeholder={t("contact.referral")} className="rounded-md" />
            </Form.Item>

            <Form.Item name="description" label={t("contact.description")} rules={[{ required: true }]}>
              <Input.TextArea
                rows={4}
                placeholder={t("contact.description")}
                className="rounded-md resize-none"
              />
            </Form.Item>

            <div className="flex flex-col items-center space-y-4 mt-6">
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                className="w-full md:w-auto px-12 font-semibold rounded-full"
              >
                {t("contact.sendMessage")}
              </Button>

              <div className="text-center text-[15px] text-gray-500">
                {t("contact.checkFaqPrefix")}{" "}
                <span
                  onClick={() => setFaqOpen(true)}
                  className="text-[var(--blue-text)] font-medium hover:underline cursor-pointer"
                >
                  FAQ
                </span>
                {t("contact.checkFaqSuffix")}
              </div>
            </div>
          </Form>
        </div>
      </div>

      {/* FOOTER */}
      <footer className="w-full py-8 cursor-pointer flex flex-col items-center justify-center text-[14px]">
        <div className="flex flex-wrap items-center justify-center text-gray-500">
          {isLoggedIn ? (
            <a className="px-[6px] hover:text-[var(--blue-text)]" onClick={() => setAccountOpen(true)}>
              {t("home.footer_text1")}
            </a>
          ) : (
            <a className="px-[6px] hover:text-[var(--blue-text)]" onClick={clickLogin}>
              {t("home.footer_text2")}
            </a>
          )}
          <span className="text-gray-400 px-1">|</span>
          <a className="px-[6px] hover:text-[var(--blue-text)]" onClick={() => setFaqOpen(true)}>
            {t("home.faq")}
          </a>
          <span className="text-gray-400 px-1">|</span>
          <a
            className="px-[6px] hover:text-[var(--blue-text)]"
            onClick={() => window.open("https://www.linkedin.com/company/civibot")}
          >
            {t("home.footer_text4")}
          </a>
          <span className="text-gray-400 px-1">|</span>
          <a className="px-[6px] hover:text-[var(--blue-text)]" onClick={changeLanguage}>
            {t("selectLang")}
          </a>
        </div>
        <div className="footer-desc w-full text-center text-gray-500 text-[14px] mt-3">{t("footer")}</div>
      </footer>
    </div>
  );
}